/**
 * Shared interface text: states, availability, units and the shell (navigation, header, sign-in). Keys are prefixed
 * "common." or "nav." or "auth.". Plural messages come in three forms (_one, _few, _other); English uses _few = _other.
 *
 * Standard abbreviations (DAU, WAU, MAU, API, CSV, JSON, XLSX, GA4, AI, GTIN) stay as they are in Serbian, with an
 * explanation in "common.abbr.*" where a reader may not know them.
 */
export const en = {
  // States
  'common.na': 'N/A',
  'common.never': 'Never',
  'common.none': 'None',
  'common.yes': 'yes',
  'common.no': 'no',
  'common.all': 'All',
  'common.loading': 'Loading analytics',
  'common.loadingPage': 'Loading page',
  'common.retry': 'Retry',
  'common.close': 'Close',
  'common.search': 'Search',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.of': 'of',
  'common.pageOf': 'Page {page} of {pages}',
  'common.unavailable': 'Unavailable',
  'common.estimated': 'Estimated',
  'common.measured': 'Measured',
  'common.actual': 'Actual',
  'common.provisional': 'provisional',
  'common.requestFailed': 'The request failed. No values are being inferred.',
  'common.error.401': 'Your session has expired. Sign in again.',
  'common.error.403': 'Your analytics role cannot open this section.',
  'common.error.404': 'Not found.',
  'common.error.429': 'Too many requests. Wait a little and try again.',
  'common.error.failed': 'Analytics request failed.',
  'common.analyticsUnavailable': 'Analytics unavailable',
  'common.needsApi': 'Needs the analytics API',
  'common.needsApiText': 'Analytics 2.0 sections read only from the API. This build runs on mock data, so they stay empty instead of showing invented numbers.',
  'common.noSource': 'No source answered',
  'common.noSourceText': 'None of the sources this section reads is available. Nothing is shown rather than zeros.',
  'common.noChartData': 'No chart data for this period',
  'common.dataSources': 'Data sources',
  'common.fresh': 'Fresh: {time}',
  'common.rows_one': '{count} row',
  'common.rows_few': '{count} rows',
  'common.rows_other': '{count} rows',
  'common.events_one': '{count} event',
  'common.events_few': '{count} events',
  'common.events_other': '{count} events',
  'common.accounts_one': '{count} account',
  'common.accounts_few': '{count} accounts',
  'common.accounts_other': '{count} accounts',

  // Availability of a metric (types/v2 Availability2)
  'common.availability.available': 'Measured',
  'common.availability.not_collected': 'Not collected',
  'common.availability.not_matured': 'Not matured',
  'common.availability.suppressed': 'Suppressed',
  'common.availability.not_configured': 'Not configured',
  // Source status (types/v2 SourceStatus)
  'common.source.ready': 'ready',
  'common.source.unavailable': 'unavailable',
  'common.source.not_configured': 'not configured',
  'common.source.error': 'error',

  // Metric card
  'common.metric.definitionOf': 'Definition of {name}',
  'common.metric.partsOf': '{numerator} of {denominator}',
  'common.metric.vsPrevious': '{change} vs previous ({previous})',
  'common.metric.noPrevious': 'No measured previous period',
  'common.metric.fromZero': 'from 0',
  'common.metric.noChange': 'no change',
  'common.metric.formula': 'Formula',
  'common.metric.counts': 'Counts',
  'common.metric.source': 'Source',
  'common.metric.period': 'Period',
  'common.metric.reliability': 'Reliability',
  'common.metric.replaces': 'Replaces "{id}": {reason}',

  // Populations and reliability (registry enums)
  'common.population.devices': 'devices (app installs)',
  'common.population.accounts': 'accounts',
  'common.population.events': 'events',
  'common.population.records': 'records',
  'common.population.money': 'money',
  'common.population.catalog': 'retail catalog rows',
  'common.reliability.verified': 'verified',
  'common.reliability.proxy': 'proxy',
  'common.reliability.estimated': 'estimated',
  'common.reliability.limited': 'limited',

  // Units
  'common.unit.days': '{value} d',
  'common.unit.seconds': '{value} s',
  'common.unit.minutes': '{value} m',
  'common.unit.hours': '{value} h',
  'common.unit.ms': '{value} ms',

  // Abbreviations kept as they are, with an explanation
  'common.abbr.DAU': 'Daily active users',
  'common.abbr.WAU': 'Weekly active users',
  'common.abbr.MAU': 'Monthly active users',
  'common.abbr.API': 'Application programming interface: the analytics server this dashboard reads from',
  'common.abbr.CSV': 'Comma-separated values, a plain text table',
  'common.abbr.JSON': 'JavaScript Object Notation, a machine-readable format',
  'common.abbr.XLSX': 'Excel workbook',
  'common.abbr.GA4': 'Google Analytics 4',
  'common.abbr.GTIN': 'Global Trade Item Number, the barcode number of a product',
  'common.abbr.RSD': 'Serbian dinar',
  'common.abbr.USD': 'US dollar',

  // Roles
  'common.role.admin': 'admin',
  'common.role.analyst': 'analyst',
  'common.role.commercial': 'commercial',

  // Language switcher
  'common.language': 'Language',
  'common.language.sr': 'Srpski',
  'common.language.en': 'English',

  // Navigation and shell
  'nav.sections': 'Sections',
  'nav.group.executive': 'Executive overview',
  'nav.group.users': 'Users',
  'nav.group.acquisition': 'Acquisition and activation',
  'nav.group.engagement': 'Engagement and retention',
  'nav.group.features': 'Features',
  'nav.group.retail': 'Retail intelligence',
  'nav.group.exports': 'Data and exports',
  'nav.group.system': 'System health and costs',
  'nav.executive': 'Executive overview',
  'nav.summary': 'Product summary',
  'nav.users': 'Users',
  'nav.acquisition': 'Installs',
  'nav.activation': 'Install funnel',
  'nav.accountActivation': 'Account activation',
  'nav.engagement': 'Engagement',
  'nav.retention': 'Device retention',
  'nav.accountRetention': 'Account retention',
  'nav.features': 'Features',
  'nav.receipts': 'Receipts',
  'nav.priceSearch': 'Price search',
  'nav.retail': 'Retail intelligence',
  'nav.exports': 'Data explorer and exports',
  'nav.reliability': 'Reliability',
  'nav.costs': 'Costs',
  'nav.privacyAdmin': 'Admin: identifiers visible',
  'nav.privacyOther': 'No direct identifiers',
  'nav.signOut': 'Sign out',
  'nav.mockBadge': 'MOCK DATA · NOT PRODUCTION',
  'nav.apiBadge': 'API DATA',
  'nav.devBadge': 'DEV AUTH BYPASS',

  // Sign-in screen
  'auth.checking': 'Checking access',
  'auth.forbiddenTitle': 'Analytics access required',
  'auth.configTitle': 'Authentication not configured',
  'auth.privateTitle': 'Private analytics',
  'auth.forbiddenText': 'Your account is valid, but it has no analytics role (admin, analyst or commercial).',
  'auth.configText': 'Set the Firebase web environment variables before opening this dashboard.',
  'auth.signInText': 'Sign in with an approved Aruha account.',
  'auth.otherAccount': 'Use another account',
  'auth.signIn': 'Sign in with Google',
};

export const sr: Record<keyof typeof en, string> = {
  'common.na': 'N/D',
  'common.never': 'Nikad',
  'common.none': 'Nema',
  'common.yes': 'da',
  'common.no': 'ne',
  'common.all': 'Sve',
  'common.loading': 'Učitavanje analitike',
  'common.loadingPage': 'Učitavanje stranice',
  'common.retry': 'Pokušaj ponovo',
  'common.close': 'Zatvori',
  'common.search': 'Pretraga',
  'common.previous': 'Prethodna',
  'common.next': 'Sledeća',
  'common.of': 'od',
  'common.pageOf': 'Strana {page} od {pages}',
  'common.unavailable': 'Nije dostupno',
  'common.estimated': 'Procena',
  'common.measured': 'Izmereno',
  'common.actual': 'Stvarno',
  'common.provisional': 'privremeno',
  'common.requestFailed': 'Zahtev nije uspeo. Nijedna vrednost se ne pretpostavlja.',
  'common.error.401': 'Sesija je istekla. Prijavi se ponovo.',
  'common.error.403': 'Tvoja analitička uloga ne može da otvori ovaj odeljak.',
  'common.error.404': 'Nije pronađeno.',
  'common.error.429': 'Previše zahteva. Sačekaj malo pa pokušaj ponovo.',
  'common.error.failed': 'Analitički zahtev nije uspeo.',
  'common.analyticsUnavailable': 'Analitika nije dostupna',
  'common.needsApi': 'Potreban je analitički API',
  'common.needsApiText': 'Odeljci Analitike 2.0 čitaju podatke samo sa API-ja. Ova verzija radi na probnim podacima, pa ostaju prazni umesto da prikazuju izmišljene brojeve.',
  'common.noSource': 'Nijedan izvor nije odgovorio',
  'common.noSourceText': 'Nijedan izvor koji ovaj odeljak čita nije dostupan. Bolje ništa nego nule.',
  'common.noChartData': 'Nema podataka za grafikon u ovom periodu',
  'common.dataSources': 'Izvori podataka',
  'common.fresh': 'Ažurno: {time}',
  'common.rows_one': '{count} red',
  'common.rows_few': '{count} reda',
  'common.rows_other': '{count} redova',
  'common.events_one': '{count} događaj',
  'common.events_few': '{count} događaja',
  'common.events_other': '{count} događaja',
  'common.accounts_one': '{count} nalog',
  'common.accounts_few': '{count} naloga',
  'common.accounts_other': '{count} naloga',

  'common.availability.available': 'Izmereno',
  'common.availability.not_collected': 'Nije prikupljeno',
  'common.availability.not_matured': 'Još nije sazrelo',
  'common.availability.suppressed': 'Prikriveno',
  'common.availability.not_configured': 'Nije podešeno',
  'common.source.ready': 'spremno',
  'common.source.unavailable': 'nedostupno',
  'common.source.not_configured': 'nije podešeno',
  'common.source.error': 'greška',

  'common.metric.definitionOf': 'Definicija: {name}',
  'common.metric.partsOf': '{numerator} od {denominator}',
  'common.metric.vsPrevious': '{change} u odnosu na prethodni period ({previous})',
  'common.metric.noPrevious': 'Prethodni period nije izmeren',
  'common.metric.fromZero': 'sa 0',
  'common.metric.noChange': 'bez promene',
  'common.metric.formula': 'Formula',
  'common.metric.counts': 'Broji',
  'common.metric.source': 'Izvor',
  'common.metric.period': 'Period',
  'common.metric.reliability': 'Pouzdanost',
  'common.metric.replaces': 'Zamenjuje „{id}”: {reason}',

  'common.population.devices': 'uređaje (instalacije aplikacije)',
  'common.population.accounts': 'naloge',
  'common.population.events': 'događaje',
  'common.population.records': 'zapise',
  'common.population.money': 'novac',
  'common.population.catalog': 'redove maloprodajnog kataloga',
  'common.reliability.verified': 'provereno',
  'common.reliability.proxy': 'posredni pokazatelj',
  'common.reliability.estimated': 'procena',
  'common.reliability.limited': 'ograničeno',

  'common.unit.days': '{value} d.',
  'common.unit.seconds': '{value} s',
  'common.unit.minutes': '{value} min',
  'common.unit.hours': '{value} h',
  'common.unit.ms': '{value} ms',

  'common.abbr.DAU': 'Dnevno aktivni korisnici (Daily Active Users)',
  'common.abbr.WAU': 'Nedeljno aktivni korisnici (Weekly Active Users)',
  'common.abbr.MAU': 'Mesečno aktivni korisnici (Monthly Active Users)',
  'common.abbr.API': 'Programski interfejs: analitički server sa kog ova kontrolna tabla čita podatke',
  'common.abbr.CSV': 'Vrednosti razdvojene zarezom ili tačkom-zarezom, tabela u običnom tekstu',
  'common.abbr.JSON': 'Format za mašinsku obradu (JavaScript Object Notation)',
  'common.abbr.XLSX': 'Excel radna sveska',
  'common.abbr.GA4': 'Google Analytics 4',
  'common.abbr.GTIN': 'Globalni broj trgovinske jedinice, broj sa bar-koda proizvoda',
  'common.abbr.RSD': 'srpski dinar',
  'common.abbr.USD': 'američki dolar',

  'common.role.admin': 'administrator',
  'common.role.analyst': 'analitičar',
  'common.role.commercial': 'komercijala',

  'common.language': 'Jezik',
  'common.language.sr': 'Srpski',
  'common.language.en': 'English',

  'nav.sections': 'Odeljci',
  'nav.group.executive': 'Pregled za upravu',
  'nav.group.users': 'Korisnici',
  'nav.group.acquisition': 'Pridobijanje i aktivacija',
  'nav.group.engagement': 'Angažovanje i zadržavanje',
  'nav.group.features': 'Funkcije',
  'nav.group.retail': 'Maloprodajna analitika',
  'nav.group.exports': 'Podaci i izvoz',
  'nav.group.system': 'Stanje sistema i troškovi',
  'nav.executive': 'Pregled za upravu',
  'nav.summary': 'Sažetak proizvoda',
  'nav.users': 'Korisnici',
  'nav.acquisition': 'Instalacije',
  'nav.activation': 'Levak instalacija',
  'nav.accountActivation': 'Aktivacija naloga',
  'nav.engagement': 'Angažovanje',
  'nav.retention': 'Zadržavanje uređaja',
  'nav.accountRetention': 'Zadržavanje naloga',
  'nav.features': 'Funkcije',
  'nav.receipts': 'Računi',
  'nav.priceSearch': 'Pretraga cena',
  'nav.retail': 'Maloprodajna analitika',
  'nav.exports': 'Pregled podataka i izvoz',
  'nav.reliability': 'Pouzdanost',
  'nav.costs': 'Troškovi',
  'nav.privacyAdmin': 'Administrator: identifikatori su vidljivi',
  'nav.privacyOther': 'Bez direktnih identifikatora',
  'nav.signOut': 'Odjavi se',
  'nav.mockBadge': 'PROBNI PODACI · NIJE PRODUKCIJA',
  'nav.apiBadge': 'PODACI SA API-JA',
  'nav.devBadge': 'RAZVOJNI REŽIM BEZ PRIJAVE',

  'auth.checking': 'Provera pristupa',
  'auth.forbiddenTitle': 'Potreban je pristup analitici',
  'auth.configTitle': 'Prijava nije podešena',
  'auth.privateTitle': 'Privatna analitika',
  'auth.forbiddenText': 'Nalog je ispravan, ali nema analitičku ulogu (administrator, analitičar ili komercijala).',
  'auth.configText': 'Podesi Firebase promenljive okruženja za veb pre otvaranja ove kontrolne table.',
  'auth.signInText': 'Prijavi se odobrenim Aruha nalogom.',
  'auth.otherAccount': 'Koristi drugi nalog',
  'auth.signIn': 'Prijava preko Google naloga',
};
